import { FunctionComponent, memo, useMemo } from "react";
import { Field } from "formik";
import { DateFilter } from "@edgetier/date-filter";
import { SelectEnum, SelectField } from "@edgetier/select";
import { FieldInline, LabeledField } from "@edgetier/client-components";
import { unreachableCaseError } from "@edgetier/utilities";
import RemoveFieldButton from "../remove-field-button/remove-field-button";
import type { IProps } from "./field-factory.types";

/**
 * Component for generating fields based on their type in a form.
 * @param {object} props.field - The field configuration.
 * @param {boolean} props.canBeRemoved - Flag indicating if the field can be removed.
 * @param {function} props.onRemove - Function to handle field removal.
 * @returns {JSX.Element} FieldFactory component.
 */
const FieldFactory: FunctionComponent<IProps> = ({ field, canBeRemoved, onRemove }) => {
    const removeButton = useMemo(
        () => (canBeRemoved ? <RemoveFieldButton onClick={onRemove} /> : null),
        [canBeRemoved, onRemove]
    );
    const fieldComponent = useMemo(() => {
        switch (field.type) {
            case "date-range": {
                const { type, ...otherFilterProps } = field;
                return (
                    <LabeledField name={otherFilterProps.startDateName} label={otherFilterProps.label}>
                        {removeButton}
                        <DateFilter {...otherFilterProps} />
                    </LabeledField>
                );
            }
            case "select": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <LabeledField name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <FieldInline icon={icon}>
                            <SelectField {...otherFilterProps} />
                        </FieldInline>
                    </LabeledField>
                );
            }
            case "select-enum": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <LabeledField name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <FieldInline icon={icon}>
                            <SelectEnum {...otherFilterProps} />
                        </FieldInline>
                    </LabeledField>
                );
            }
            case "search": {
                const { type, icon, fieldType, ...otherFilterProps } = field;
                return (
                    <LabeledField name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <FieldInline icon={icon}>
                            <Field type={fieldType} {...otherFilterProps} />
                        </FieldInline>
                    </LabeledField>
                );
            }
            case "custom": {
                const { type, icon, ...otherFilterProps } = field;
                return (
                    <LabeledField name={otherFilterProps.name} label={otherFilterProps.label}>
                        {removeButton}
                        <FieldInline icon={icon}>{field.render}</FieldInline>
                    </LabeledField>
                );
            }

            default: {
                unreachableCaseError(field);
                return null;
            }
        }
    }, [field, removeButton]);

    return <div data-testid={`filter-${field.id}`}>{fieldComponent}</div>;
};

export default memo(FieldFactory);
